<template>
  <div class="q-gutter-x-sm flex items-center">
    <q-btn flat round color="primary" icon="search" @click="searchOpen = true" />

    <q-dialog
      v-model="searchOpen"
      position="top"
      transition-show="jump-down"
      transition-hide="jump-up"
      backdrop-filter="blur(5px)"
    >
      <!-- Μεγαλύτερο margin-top για να "επιπλέει" πιο κεντρικά, στρογγυλεμένες γωνίες -->
      <q-card
        style="width: 650px; max-width: 95vw; margin-top: 10vh; border-radius: 16px"
        class="shadow-10"
      >
        <!-- Το QSelect παίρνει όλο τον χώρο της κάρτας -->
        <q-select
          ref="searchInputRef"
          v-model="selectedResult"
          use-input
          hide-selected
          fill-input
          borderless
          behavior="menu"
          class="text-h6 q-px-md q-py-sm"
          popup-content-class="search-dropdown-menu"
          :popup-content-style="{
            overflowX: 'hidden',
            width: '614px',
            maxWidth: 'calc(95vw - 36px)',
          }"
          input-debounce="600"
          :options="liveResults"
          :placeholder="$t('navbar.search')"
          @filter="onLiveSearch"
          @keyup.enter="onSearch"
          @update:model-value="onSelectProgramme"
          @virtual-scroll="onVirtualScroll"
          virtual-scroll-item-size="72"
          virtual-scroll-slice-size="20"
          color="primary"
          autofocus
        >
          <template v-slot:prepend>
            <q-icon name="search" color="primary" size="sm" />
          </template>

          <template v-slot:append>
            <div class="row items-center">
              <!-- Ένα μικρό "PRO" hint για το Esc -->
              <span
                class="text-caption text-grey-5 q-mr-sm"
                style="font-size: 10px; font-weight: bold"
                >ESC</span
              >
              <q-btn
                flat
                round
                dense
                icon="close"
                color="grey-6"
                size="sm"
                @click="searchOpen = false"
              />
            </div>
          </template>

          <!-- Πώς φαίνεται το κάθε αποτέλεσμα -->
          <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps" class="q-py-md text-grey-9 search-item">
              <q-item-section style="min-width: 0; overflow: hidden">
                <q-item-label
                  lines="2"
                  class="text-subtitle1 text-weight-bold"
                  style="white-space: normal; word-break: break-word"
                >
                  {{ getName(scope.opt) }}
                </q-item-label>

                <q-item-label caption lines="1" class="text-body2 text-primary q-mt-xs">
                  <q-icon name="school" size="xs" class="q-mr-xs" /> {{ getType(scope.opt) }}
                  <span class="q-mx-sm text-grey-4">|</span>
                  <q-icon name="place" size="xs" class="q-mr-xs" /> {{ getCity(scope.opt) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>

          <!-- Αν δεν υπάρχουν αποτελέσματα -->
          <template v-slot:no-option>
            <q-item
              v-if="searchKeyword && searchKeyword.length >= 2 && !isSearching"
              class="q-pa-xl flex flex-center"
            >
              <q-item-section class="text-grey-6 text-center">
                <q-icon name="search_off" size="xl" class="q-mx-auto q-mb-sm opacity-50" />
                <span class="text-h6">{{ $t('navbar.no_results_found') }}</span>
              </q-item-section>
            </q-item>
            <!-- Αρχική κατάσταση (Άδειο input) -->
            <q-item v-else-if="!searchKeyword" class="q-pa-xl flex flex-center">
              <q-item-section class="text-grey-5 text-center">
                <q-icon name="manage_search" size="xl" class="q-mx-auto q-mb-sm opacity-50" />
                <span class="text-h6">{{ $t('navbar.search_hint') }}</span>
              </q-item-section>
            </q-item>
          </template>
          <template v-slot:after-options>
            <div v-if="isLoadingMore" class="q-pa-md flex flex-center">
              <q-spinner-dots color="primary" size="2em" />
            </div>
          </template>
        </q-select>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { QSelect } from 'quasar'
import { useProgrammeLocale } from 'src/composables/programme/useProgrammeLocale'
import { useProgrammeRoute } from 'src/composables/programme/useProgrammeRoute'
import { useProgrammeService } from 'src/services/ProgrammeService'
import type { ProgrammeListItem } from 'src/types/Programme'
import { nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'

const { navigateToRouteDetails } = useProgrammeRoute()
const programmeService = useProgrammeService()
const router = useRouter()
const { getName, getCity, getType } = useProgrammeLocale()

const searchOpen = ref(false)
const searchInputRef = ref<QSelect | null>(null)
const selectedResult = ref<ProgrammeListItem | null>(null)
const searchKeyword = ref('')
const liveResults = ref<ProgrammeListItem[]>([])
const isSearching = ref(false)

const currentPage = ref(1)
const hasNextPage = ref(false)
const isLoadingMore = ref(false)
const PAGE_LIMIT = 22

const onLiveSearch = async (val: string, update: (callback: () => void) => void) => {
  searchKeyword.value = val

  if (val.length < 2) {
    update(() => {
      liveResults.value = []
      hasNextPage.value = false // Reset
    })
    return
  }

  isSearching.value = true
  currentPage.value = 1

  try {
    const response = await programmeService.getProgrammes(
      { page: currentPage.value, limit: PAGE_LIMIT },
      { q: val, is_applicable: true }
    )

    update(() => {
      liveResults.value = response.items
      hasNextPage.value = response.has_next // Το αποθηκεύουμε για το scroll
    })
  } catch (error) {
    console.error('Live search error:', error)
    update(() => {
      liveResults.value = []
    })
  } finally {
    isSearching.value = false
  }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onVirtualScroll = async (details: any) => {
  const { to, direction } = details
  console.log(to)
  if (
    direction === 'increase' &&
    to >= liveResults.value.length - 1 &&
    hasNextPage.value &&
    !isLoadingMore.value
  ) {
    isLoadingMore.value = true
    currentPage.value++

    try {
      const response = await programmeService.getProgrammes(
        { page: currentPage.value, limit: PAGE_LIMIT },
        { q: searchKeyword.value, is_applicable: true }
      )

      liveResults.value.push(...response.items)
      hasNextPage.value = response.has_next
    } catch (error) {
      console.error('Pagination error:', error)
      currentPage.value--
    } finally {
      await nextTick()
      isLoadingMore.value = false
    }
  }
}

const onSelectProgramme = async (programme: ProgrammeListItem) => {
  if (!programme) return

  selectedResult.value = null
  searchKeyword.value = ''
  searchOpen.value = false

  await navigateToRouteDetails(programme)
}

const onSearch = async () => {
  const query = searchKeyword.value.trim()
  if (!query) return

  searchOpen.value = false

  await router.push({
    name: 'Programmes',
    query: { q: query },
  })
}
</script>
<style>
.search-dropdown-menu {
  border-radius: 0 0 16px 16px !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1) !important;
  border-top: 1px solid #f0f0f0;

  /* ← ADD THESE */
  overflow-x: hidden !important;
  width: 614px !important;
  max-width: calc(95vw - 36px) !important;
  box-sizing: border-box;
}

.search-item:hover {
  background-color: #f8f9fa !important;
}
</style>
